import { PLATFORM_ID, Injectable, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Wave } from '@foobar404/wave';
import { Store } from '@ngrx/store';
import { first } from 'rxjs';
import { AnalyticsService } from '../analytics.service';
import { AppState } from '../../app.state';
import { MediaPlayerState } from '../../enum/media-player/media-player-state';
import { MediaContent } from '../../objects/media-player/media-content';
import { MediaPlayerCurrentState } from '../../objects/media-player/media-player-current-state';
import { MediaRadio } from '../../objects/media-player/media-radio';
import { updateMediaContentGeneral } from '../../player.actions';
import { selectStaticMediaPlayerSelector } from '../../player.selectors';
import { RadioMetadataFetcherService } from '../radio-metadata-fetcher.service';
import { selectEqualizerState } from '../../equalizer.selectors';
import { EqualizerService } from './equalizer.service';

/** One way of trying to play a station: which element, with which URL. */
interface PlaybackSource {
  element: HTMLAudioElement;
  url: string;
}

@Injectable({
  providedIn: 'root',
})
export class MediaPlayerService {

  /**
   * Playback is browser-only. During prerendering there is no `Audio`
   * constructor and nothing can start playing, so the elements are left unset
   * and every browser-only side effect below is skipped.
   */
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private audioPlayer: HTMLAudioElement = this.isBrowser
    ? new Audio()
    : (null as unknown as HTMLAudioElement);
  private audioPlayerWaveform: HTMLAudioElement = this.isBrowser
    ? new Audio()
    : (null as unknown as HTMLAudioElement);

  /**
   * Plays stations whose server sends no CORS headers. Those can't play on
   * `audioPlayer`: its `crossOrigin` makes the browser drop the response, and
   * an element wired into Web Audio outputs only silence for a non-CORS
   * source anyway. This one is never connected to Web Audio and never gets
   * `crossOrigin`, so it plays such streams — without the equalizer.
   */
  private plainAudioPlayer: HTMLAudioElement = this.isBrowser
    ? new Audio()
    : (null as unknown as HTMLAudioElement);

  /** The element the current station plays on. */
  private activePlayer: HTMLAudioElement = this.audioPlayer;

  /** Bumped on every play and stop, so late results of an older play are ignored. */
  private playAttempt = 0;

  /**
   * True while a play still has sources left to try. Errors fired meanwhile
   * are left to the `play()` rejection, which moves on to the next source.
   */
  private canFallBack = false;

  /**
   * Stream URLs that failed on the equalizer element but played on the plain
   * one — they lack CORS headers, so later plays (a resume, say) go straight
   * to the plain element.
   */
  private readonly noCorsUrls = new Set<string>();

  private readonly equalizerBypassedState = signal(false);
  /** True while the current station plays on the plain element, outside the equalizer. */
  readonly equalizerBypassed = this.equalizerBypassedState.asReadonly();

  private progressIntervalId: number | NodeJS.Timeout | undefined;

  private mediaPlayerState$: any;
  private wave: Wave | undefined;

  /** Registered by `AudioWaveformComponent` once its canvas is in the DOM. */
  public static audioWaveformCanvasElement: HTMLCanvasElement | undefined;

  constructor(
    private store: Store<AppState>,
    private radioMetadataFetcherService: RadioMetadataFetcherService,
    private analytics: AnalyticsService,
    private equalizerService: EqualizerService
  ) {
    if (!this.isBrowser) {
      return;
    }

    // Both feed Web Audio (the equalizer and the waveform), which needs CORS.
    // Set before any `src`, as it only applies to loads that start after it.
    this.audioPlayer.crossOrigin = 'anonymous';
    this.audioPlayerWaveform.crossOrigin = 'anonymous';

    // Set audio player listeners.
    this.setAudioListeners();

    // Set reocuring interval to log that something is playing on each 15 mins.
    // This way we would have more accruate metric for how much people are using this web app!
    setInterval(() => {
      this.store
        .select(selectStaticMediaPlayerSelector)
        .pipe(first())
        .subscribe((mediaContent: MediaPlayerCurrentState) => {
          this.analytics.logEvent('hxPlayerWebActiveSession', {
            session_player_state: mediaContent.playerState,
          });
        });
    }, 15 * 60 * 1000);
  }

  getAudioElement(): HTMLAudioElement {
    return this.activePlayer;
  }

  play(mediaContent: MediaContent | null) {
    if (mediaContent !== null && mediaContent instanceof MediaRadio) {
      this.store.dispatch(
        updateMediaContentGeneral({
          currentStreamTitle: null,
          currentStreamImageUrl: null,
          playerState: MediaPlayerState.LOADING,
          progress: 0,
          mediaFormat: null,
          audioChannels: null,
          bitrateFromSource: null,
          sampleFrequency: null,
        })
      );

      this.radioMetadataFetcherService?.stopRadioMetadataWithLibs();
      this.audioPlayer.pause();
      this.plainAudioPlayer.pause();
      this.releaseElement(this.audioPlayerWaveform);

      // Actuall playing of the media item!
      var mediaRadio = mediaContent as MediaRadio;

      let srcUrl = mediaRadio.url.includes('https')
        ? mediaRadio.url
        : mediaRadio.url.replace('http', 'https');
      console.log('Playing: ', srcUrl);

      // The equalizer element first. When the stream can't play there —
      // usually a server without CORS headers — the plain element, and last
      // the station's original URL in case the https upgrade broke it.
      const sources: PlaybackSource[] = [];
      if (!this.noCorsUrls.has(srcUrl)) {
        sources.push({ element: this.audioPlayer, url: srcUrl });
      }
      sources.push({ element: this.plainAudioPlayer, url: srcUrl });
      if (mediaRadio.url !== srcUrl) {
        sources.push({ element: this.plainAudioPlayer, url: mediaRadio.url });
      }

      this.playFrom(mediaRadio, sources, ++this.playAttempt);

      // Log media item played.
      this.analytics.logEvent('hxPlayerWebPlayedRadio', {
        radio_name: mediaRadio.name,
      });
    } else {
      // Tracks for the future.
    }
  }

  /**
   * Tries `sources` in order until one plays. A source that fails is released
   * so it stops downloading, and the next one is tried; when none are left
   * the player goes into the error state.
   *
   * `equalizerFailedUrl` is the URL the equalizer element already failed on
   * during this play. If the plain element then plays that same URL, the
   * server lacks CORS headers, which is remembered for later plays.
   */
  private playFrom(
    station: MediaRadio,
    sources: PlaybackSource[],
    attempt: number,
    equalizerFailedUrl: string | null = null
  ) {
    const [source, ...fallbacks] = sources;
    const element = source.element;
    const withEqualizer = element === this.audioPlayer;

    if (element !== this.activePlayer) {
      this.releaseElement(this.activePlayer);
      this.activePlayer = element;
    }
    this.canFallBack = fallbacks.length > 0;

    element.src = source.url;
    if (withEqualizer) {
      // Route playback through the equalizer before it starts, so the
      // remembered preset is already in effect on the very first radio.
      this.attachEqualizer();
    }

    // Two-argument `then`, so only a failed `play()` falls back — not an
    // exception from the start-up work (the waveform, say) once it plays.
    element.play().then(
      () => {
        if (attempt !== this.playAttempt) {
          return;
        }
        if (source.url === equalizerFailedUrl) {
          this.noCorsUrls.add(source.url);
        }
        this.onPlaybackStarted(station, source.url, withEqualizer);
      },
      (error: DOMException) => {
        if (attempt !== this.playAttempt) {
          return;
        }
        console.log('PlayPromise error!', error.message);
        // If error occured cause User wanted to play new radio or paused, so be it, dont retry!
        if (this.wasInterrupted(error)) {
          console.log('Skiping retry as it was interrupted!');
          return;
        }

        if (fallbacks.length > 0) {
          console.log(
            withEqualizer
              ? 'Stream can not play through the equalizer (likely no CORS headers), playing it without.'
              : 'Retrying with the next source.'
          );
          this.playFrom(
            station,
            fallbacks,
            attempt,
            withEqualizer ? source.url : equalizerFailedUrl
          );
          return;
        }

        this.canFallBack = false;
        this.equalizerBypassedState.set(false);
        this.dispatchError();
        console.log('playPromiseRetry error!', error);
      });
  }

  private onPlaybackStarted(station: MediaRadio, url: string, withEqualizer: boolean) {
    this.canFallBack = false;
    this.equalizerBypassedState.set(!withEqualizer);

    clearInterval(this.progressIntervalId);
    this.updateProgress();
    this.dispatchPlaying();

    // Metadata stuff.
    this.radioMetadataFetcherService.stopAndStartRadioMetadataWithLibs(station, url);

    // The waveform reads the stream through Web Audio as well, so it only
    // works for streams that played on the equalizer element.
    if (withEqualizer) {
      this.startWaveform(url);
    }
  }

  private startWaveform(url: string) {
    const canvas = MediaPlayerService.audioWaveformCanvasElement;
    if (canvas == null) {
      return;
    }

    this.audioPlayerWaveform.src = url;
    this.audioPlayerWaveform
      .play()
      .catch((error: DOMException) => console.log('Waveform play error!', error.message));
    this.wave = new Wave(
      this.audioPlayerWaveform,
      canvas,
      true // Must be muted, cause this is only waveform audio!
    );
    // Simple example: add an animation
    this.wave.addAnimation(
      new this.wave.animations.Shine({
        lineColor: 'red',
      })
    );
  }

  private wasInterrupted(error: DOMException): boolean {
    return (
      error.message.includes('The play() request was interrupted by a new load') ||
      error.message.includes('The play() request was interrupted by a call to pause()')
    );
  }

  /** Stops an element and drops its stream, so it no longer downloads. */
  private releaseElement(element: HTMLAudioElement) {
    element.pause();
    element.removeAttribute('src');
    element.load();
  }

  /**
   * Builds the equalizer graph around the audio element and applies the
   * stored preset.
   *
   * Done here, on play, rather than at app start for two reasons: a play is a
   * user gesture, so the AudioContext is allowed to run (one created earlier
   * stays suspended and would silence the element it is wired to), and the
   * element is by then set up for the stream. It is a no-op after the first
   * call apart from resuming a suspended context.
   */
  private attachEqualizer() {
    if (!this.isBrowser || !this.equalizerService.isSupported()) {
      return;
    }

    if (!this.equalizerService.attach(this.audioPlayer)) {
      return;
    }

    this.store
      .select(selectEqualizerState)
      .pipe(first())
      .subscribe(equalizer => {
        this.equalizerService.applyState(equalizer.currentPreset, equalizer.isEnabled);
      });
  }

  private dispatchPlaying() {
    this.store.dispatch(
      updateMediaContentGeneral({
        currentStreamTitle: null,
        currentStreamImageUrl: null,
        playerState: MediaPlayerState.PLAYING,
        progress: this.activePlayer.currentTime,
        mediaFormat: null,
        audioChannels: null,
        bitrateFromSource: null,
        sampleFrequency: null,
      })
    );
  }

  private dispatchError() {
    this.store.dispatch(
      updateMediaContentGeneral({
        currentStreamTitle: '',
        currentStreamImageUrl: '',
        playerState: MediaPlayerState.ERROR,
        progress: 0,
        mediaFormat: '',
        audioChannels: 2,
        bitrateFromSource: 0,
        sampleFrequency: 0,
      })
    );
  }

  /**
   * Both playback elements get the same listeners, but only the active one
   * speaks for the player: the other is idle or being released.
   */
  private setAudioListeners() {
    for (const element of [this.audioPlayer, this.plainAudioPlayer]) {
      element.volume = 0.15;

      element.onplaying = (event) => {
        if (element !== this.activePlayer) {
          return;
        }
        console.log('onplaying: ' + new Date(), event);
        this.dispatchPlaying();
      };
      element.onerror = (event) => {
        // While sources are left, the failed `play()` moves on to the next one.
        if (element !== this.activePlayer || this.canFallBack) {
          return;
        }
        console.log('onerror:  ' + new Date(), event);
        this.dispatchError();
      };
      element.ondrop = (event) => {
        if (element !== this.activePlayer) {
          return;
        }
        console.log('ondrop: ' + new Date(), event);
        this.dispatchError();
      };
      element.onstalled = (event) => {
        if (element !== this.activePlayer) {
          return;
        }
        console.log('onstalled: ' + new Date(), event);
        // As this event is also fired when Player is PAUSED and Net connection goes down, we dont wanna show spinner in that case, so ...
        this.mediaPlayerState$ = this.store.select(
          selectStaticMediaPlayerSelector
        );
        this.mediaPlayerState$
          .pipe(first())
          .subscribe((mediaContent: MediaPlayerCurrentState) => {
            if (mediaContent.playerState == MediaPlayerState.PLAYING) {
              this.store.dispatch(
                updateMediaContentGeneral({
                  currentStreamTitle: null,
                  currentStreamImageUrl: null,
                  playerState: MediaPlayerState.LOADING,
                  progress: 0,
                  mediaFormat: null,
                  audioChannels: null,
                  bitrateFromSource: null,
                  sampleFrequency: null,
                })
              );
            } else {
              console.log('There was onstalled event but Player is paused!');
            }
          });
      };
      element.onpause = (event) => {
        if (element !== this.activePlayer) {
          return;
        }
        console.log('onpause: ' + new Date(), event);
        this.store.dispatch(
          updateMediaContentGeneral({
            currentStreamTitle: null,
            currentStreamImageUrl: null,
            playerState: MediaPlayerState.PAUSE,
            progress: 0,
            mediaFormat: null,
            audioChannels: null,
            bitrateFromSource: null,
            sampleFrequency: null,
          })
        );
      };
    }
  }

  resume() {
    this.mediaPlayerState$ = this.store.select(selectStaticMediaPlayerSelector);
    this.mediaPlayerState$
      .pipe(first())
      .subscribe((mediaContent: MediaPlayerCurrentState) => {
        this.play(mediaContent.mediaContent);
      });
  }

  pauseStop() {
    // Whatever play is still loading is abandoned.
    this.playAttempt++;
    this.canFallBack = false;

    this.activePlayer.currentTime = 0;
    this.activePlayer.pause();
    this.activePlayer.currentTime = 0;
    this.audioPlayerWaveform.currentTime = 0;
    this.audioPlayerWaveform.pause();
    this.audioPlayerWaveform.currentTime = 0;
    this.radioMetadataFetcherService?.stopRadioMetadataWithLibs();
    clearInterval(this.progressIntervalId);
  }

  next() {}

  previous() {}

  updateVolume(volumeValue: number) {
    this.setVolume(volumeValue);
  }

  muteVolume() {
    this.setVolume(0);
  }

  unmuteVolume() {
    this.mediaPlayerState$ = this.store.select(selectStaticMediaPlayerSelector);
    // Play/Pause button listener!
    this.mediaPlayerState$
      .pipe(first())
      .subscribe((mediaContent: MediaPlayerCurrentState) => {
        if (
          mediaContent.preMuteVolume != null &&
          mediaContent.preMuteVolume > 0
        ) {
          this.setVolume(mediaContent.preMuteVolume);
        } else {
          mediaContent.preMuteVolume = 0.15;
          this.setVolume(0.15);
        }
      });
  }

  /** Kept equal on both elements, so switching between them keeps the level. */
  private setVolume(volume: number) {
    this.audioPlayer.volume = volume;
    this.plainAudioPlayer.volume = volume;
  }

  private updateProgress() {
    this.progressIntervalId = setInterval(() => {
      this.store.dispatch(
        updateMediaContentGeneral({
          currentStreamTitle: null,
          currentStreamImageUrl: null,
          playerState: null,
          progress: this.activePlayer.currentTime,
          mediaFormat: null,
          audioChannels: null,
          bitrateFromSource: null,
          sampleFrequency: null,
        })
      );
    }, 2000);
  }
}
